import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Chip,
  Autocomplete,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SvgIcon,
} from '@mui/material';

function PreferencesTab({
  isLoadingPreferences,
  selectedSupplier,
  setSelectedSupplier,
  selectedFactories,
  setSelectedFactories,
  selectedDepartments,
  setSelectedDepartments,
  selectedCountries,
  setSelectedCountries,
  supplierSearch,
  setSupplierSearch,
  supplierResults,
  isSearching,
  searchError,
  showResults,
  setShowResults,
  availableDepartments,
  availableCountries,
  isLoadingDepartments,
  isExternalUser,
}) {
  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.business_name);
    setShowResults(false);
    setSelectedFactories([]);
    setSelectedDepartments([]);
    setSelectedCountries([]);
  };

  const handleFactoryChange = (event, newValue) => {
    setSelectedFactories(newValue);
  };

  const handleDepartmentChange = (event, newValue) => {
    setSelectedDepartments(newValue);
  };

  const handleCountryChange = (event, newValue) => {
    setSelectedCountries(newValue);
  };

  if (isLoadingPreferences) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          fontWeight={600}
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box component="span" sx={{ fontSize: '1.5rem' }}></Box>
          Preferences
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Customize your suppliers, factories, and country preferences
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Suppliers */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Suppliers
          </Typography>
          <Box
            className="supplier-search-container"
            sx={{ position: 'relative' }}
          >
            <TextField
              fullWidth
              value={supplierSearch}
              onChange={(e) => {
                setSupplierSearch(e.target.value);
                if (!e.target.value) {
                  setSelectedSupplier(null);
                  setSelectedFactories([]);
                  setSelectedDepartments([]);
                  setSelectedCountries([]);
                }
              }}
              onFocus={() => {
                if (supplierSearch && supplierResults.length > 0) {
                  setShowResults(true);
                }
              }}
              placeholder="Search suppliers..."
              variant="outlined"
              size="small"
              disabled={isExternalUser}
              InputProps={{
                startAdornment: (
                  <SvgIcon sx={{ color: 'action.active', mr: 1 }}>
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                  </SvgIcon>
                ),
                endAdornment: isSearching ? (
                  <CircularProgress size={20} />
                ) : null,
              }}
            />
            {showResults && supplierResults.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  maxHeight: 300,
                  overflow: 'auto',
                  zIndex: 1000,
                  boxShadow: 3,
                }}
              >
                <List>
                  {supplierResults.map((supplier) => (
                    <ListItem key={supplier.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleSupplierSelect(supplier)}
                      >
                        <ListItemText
                          primary={supplier.business_name}
                          secondary={supplier.vendor_id || supplier.id}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
            {searchError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {searchError}
              </Typography>
            )}
            {selectedSupplier && !isExternalUser && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={selectedSupplier.business_name}
                  onDelete={() => {
                    setSelectedSupplier(null);
                    setSupplierSearch('');
                    setSelectedFactories([]);
                    setSelectedDepartments([]);
                    setSelectedCountries([]);
                  }}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </Grid>

        {/* Factories */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Factories
          </Typography>
          <Autocomplete
            multiple
            options={selectedSupplier?.factories || []}
            getOptionLabel={(option) => option.name}
            value={selectedFactories}
            onChange={handleFactoryChange}
            disabled={!selectedSupplier}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Box
                  component="span"
                  sx={{
                    width: 17,
                    height: 17,
                    mr: 1,
                    border: '2px solid',
                    borderColor: selected ? 'primary.main' : 'grey.400',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                  }}
                >
                  {selected && (
                    <Box
                      component="span"
                      sx={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </Box>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  selectedSupplier
                    ? 'Select factories...'
                    : 'Select a supplier first'
                }
                size="small"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  sx={{ bgcolor: '#e3f2fd' }}
                />
              ))
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        {/* Departments */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Departments
          </Typography>
          <Autocomplete
            multiple
            options={availableDepartments}
            getOptionLabel={(option) => option.name || option.id}
            value={selectedDepartments}
            onChange={handleDepartmentChange}
            disabled={selectedFactories.length === 0 && !selectedSupplier}
            loading={isLoadingDepartments}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Box
                  component="span"
                  sx={{
                    width: 17,
                    height: 17,
                    mr: 1,
                    border: '2px solid',
                    borderColor: selected ? 'primary.main' : 'grey.400',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                  }}
                >
                  {selected && (
                    <Box
                      component="span"
                      sx={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </Box>
                {option.name || option.id}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  selectedFactories.length > 0 || selectedSupplier
                    ? 'Select departments...'
                    : 'Select factories or supplier first'
                }
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingDepartments ? (
                        <CircularProgress size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name || option.id}
                  size="small"
                  sx={{ bgcolor: '#e8f5e9' }}
                />
              ))
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        {/* Countries */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Countries
          </Typography>
          <Autocomplete
            multiple
            options={availableCountries}
            getOptionLabel={(option) => option.name}
            value={selectedCountries}
            onChange={handleCountryChange}
            disabled={selectedFactories.length === 0}
            loading={isLoadingDepartments}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Box
                  component="span"
                  sx={{
                    width: 17,
                    height: 17,
                    mr: 1,
                    border: '2px solid',
                    borderColor: selected ? 'primary.main' : 'grey.400',
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selected ? 'primary.main' : 'transparent',
                  }}
                >
                  {selected && (
                    <Box
                      component="span"
                      sx={{
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      ✓
                    </Box>
                  )}
                </Box>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={
                  selectedFactories.length > 0
                    ? 'Select countries...'
                    : 'Select factories first'
                }
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingDepartments ? (
                        <CircularProgress size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  sx={{ bgcolor: '#f3e5f5' }}
                />
              ))
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

PreferencesTab.propTypes = {
  isLoadingPreferences: PropTypes.bool.isRequired,
  selectedSupplier: PropTypes.shape({
    id: PropTypes.string,
    business_name: PropTypes.string,
    factories: PropTypes.arrayOf(PropTypes.object),
  }),
  setSelectedSupplier: PropTypes.func.isRequired,
  selectedFactories: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSelectedFactories: PropTypes.func.isRequired,
  selectedDepartments: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSelectedDepartments: PropTypes.func.isRequired,
  selectedCountries: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSelectedCountries: PropTypes.func.isRequired,
  supplierSearch: PropTypes.string.isRequired,
  setSupplierSearch: PropTypes.func.isRequired,
  supplierResults: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSearching: PropTypes.bool.isRequired,
  searchError: PropTypes.string.isRequired,
  showResults: PropTypes.bool.isRequired,
  setShowResults: PropTypes.func.isRequired,
  availableDepartments: PropTypes.arrayOf(PropTypes.object).isRequired,
  availableCountries: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoadingDepartments: PropTypes.bool.isRequired,
  isExternalUser: PropTypes.bool.isRequired,
};

export default PreferencesTab;
